/**
 * `<ToastHost>` — renders the toast queue (frontend.md §2.1, §4.2).
 *
 * Mounted once at the app root. Subscribes to `useToastStore` and renders each toast.
 * Two affordances, both from frontend.md §4.2:
 *  - `retry` — the `external`-error retry button. The frontend never auto-retries
 *    (conventions.md §7) — this re-issues the failed query on explicit user click.
 *  - `errorReport` — the `system`-error "copy error report" button. Copies the full
 *    `ErrorEnvelope` JSON for a bug report.
 */

// Import directly from the source file (not the `@/hooks` barrel) — `<ToastHost>` is a
// shared component and should not pull the data-hook barrel.
import { useToastStore } from '@/hooks/useToastStore';
import type { Toast } from '@/hooks/useToastStore';

function copyErrorReport(toast: Toast): void {
  if (toast.errorReport && typeof navigator !== 'undefined' && navigator.clipboard) {
    void navigator.clipboard.writeText(JSON.stringify(toast.errorReport, null, 2));
  }
}

export function ToastHost(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div className="toast-host" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.kind}`} role="status">
          <div className="toast__body">
            <div className="toast__title">{toast.title}</div>
            <div className="toast__message">{toast.message}</div>
          </div>
          <div className="toast__actions">
            {toast.retry !== undefined && (
              <button
                type="button"
                onClick={() => {
                  toast.retry?.();
                  dismiss(toast.id);
                }}
              >
                Retry
              </button>
            )}
            {toast.errorReport !== undefined && (
              <button type="button" onClick={() => copyErrorReport(toast)}>
                Copy report
              </button>
            )}
            <button type="button" aria-label="Dismiss" onClick={() => dismiss(toast.id)}>
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
